import { useCallback, useEffect, useRef, useState } from 'react';
import { Select, Spin } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';

// Photon (komoot's OpenStreetMap geocoder): free, keyless, CORS-enabled and —
// unlike Nominatim, whose usage policy forbids it — built for search-as-you-type.
// Overridable so a node can point at a self-hosted Photon instead.
const GEOCODER_URL = process.env.REACT_APP_GEOCODER_URL || 'https://photon.komoot.io/api';

// Photon orders purely by text match, so the village of Par beats Paris and
// Londa beats London. Re-rank bigger settlements first; the sort is stable, so
// Photon's own order still decides within a tier.
const PLACE_RANK = { city: 0, municipality: 1, town: 1, village: 2, hamlet: 3 };
const rankOf = p => PLACE_RANK[p.osm_value] ?? 2;

// "London, England, United Kingdom" — dropping a state that only repeats the
// city's name ("Berlin, Berlin, Germany").
const labelOf = p => [p.name, p.state !== p.name ? p.state : null, p.country].filter(Boolean).join(', ');

/**
 * City search for the 'geo' Person field type: type a place name, pick a
 * suggestion. Coordinates are kept alongside the label (the detail page's map
 * link, any future distance features) but are never shown or typed. Follows
 * antd's value/onChange contract so it drops into a Form.Item like any input.
 * Value shape: { label: string, lat: number, lng: number } | undefined.
 * Values saved by the earlier map picker have no label and show as coordinates.
 *
 * `multiple` (the 'geo_multi' field type) picks several places, shown as
 * removable tags; the value is then an array of those objects | undefined.
 */
function GeoLocationInput({ value, onChange, multiple = false, placeholder = multiple ? 'Search for cities…' : 'Search for a city…' }) {
    const [places, setPlaces] = useState([]);
    const [searching, setSearching] = useState(false);
    // The query the latest keystroke asked for, so a slow response to an
    // earlier one can't overwrite newer suggestions.
    const latestQuery = useRef('');

    const performSearch = async (q) => {
        try {
            const res = await fetch(`${GEOCODER_URL}?q=${encodeURIComponent(q)}&limit=8&layer=city&lang=en`);
            if (!res.ok) throw new Error(`geocoder ${res.status}`);
            const data = await res.json();
            if (q !== latestQuery.current) return;
            const found = (data.features || [])
                .map(f => ({ ...f.properties, lng: f.geometry.coordinates[0], lat: f.geometry.coordinates[1] }))
                .sort((a, b) => rankOf(a) - rankOf(b))
                .map(p => ({ label: labelOf(p), lat: p.lat, lng: p.lng }));
            // Distinct OSM objects can share a label (a city and its municipality).
            setPlaces(found.filter((p, i) => found.findIndex(o => o.label === p.label) === i));
        } catch {
            if (q === latestQuery.current) setPlaces([]);
        } finally {
            if (q === latestQuery.current) setSearching(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedSearch = useCallback(debounce(performSearch, 300), []);
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleSearch = (q) => {
        const query = q.trim();
        latestQuery.current = query;
        if (query.length < 2) {
            debouncedSearch.cancel();
            setPlaces([]);
            setSearching(false);
            return;
        }
        setSearching(true);
        debouncedSearch(query);
    };

    if (multiple) {
        // Places are keyed by label (coordinates for labelless legacy values),
        // so an already-picked place is recognised when it shows up in results.
        const selected = Array.isArray(value) ? value : [];
        const keyOf = p => p.label || `${p.lat}, ${p.lng}`;
        return (
            <Select
                mode="multiple"
                showSearch
                allowClear
                labelInValue
                filterOption={false}
                placeholder={placeholder}
                suffixIcon={<EnvironmentOutlined />}
                value={selected.map(p => ({ value: keyOf(p), label: keyOf(p) }))}
                options={places.map(p => ({ value: keyOf(p), label: p.label }))}
                onSearch={handleSearch}
                onChange={opts => {
                    const next = (opts || [])
                        .map(o => selected.find(p => keyOf(p) === o.value) || places.find(p => keyOf(p) === o.value))
                        .filter(Boolean);
                    onChange?.(next.length ? next : undefined);
                }}
                notFoundContent={searching ? <Spin size="small" /> : null}
                style={{ width: '100%' }}
            />
        );
    }

    const currentLabel = value?.label
        || (value?.lat != null && value?.lng != null ? `${value.lat}, ${value.lng}` : null);

    return (
        <Select
            showSearch
            allowClear
            labelInValue
            filterOption={false}
            placeholder={placeholder}
            suffixIcon={<EnvironmentOutlined />}
            value={currentLabel ? { value: 'current', label: currentLabel } : undefined}
            options={places.map((p, i) => ({ value: String(i), label: p.label }))}
            onSearch={handleSearch}
            onChange={opt => {
                if (!opt) { onChange?.(undefined); return; }
                const place = places[Number(opt.value)];
                if (place) onChange?.(place);
            }}
            notFoundContent={searching ? <Spin size="small" /> : null}
            style={{ width: '100%' }}
        />
    );
}

export default GeoLocationInput;
